import {useRef} from 'react';

/**
 * Hook that maintains a mutable ref to the latest value, useful for accessing
 * current values in callbacks without recreating them on every render.
 *
 * This prevents stale closures in event handlers, timers, and async operations
 * while maintaining stable function references.
 *
 * @example
 * ```tsx
 * const SearchComponent = ({ onSearch }) => {
 *   const [query, setQuery] = useState('');
 *   const [isLoading, setIsLoading] = useState(false);
 *
 *   // We want access to the latest query value in our debounced search
 *   const latestQuery = useLatestRef(query);
 *
 *   // This callback stays stable across renders
 *   const debouncedSearch = useCallback(
 *     debounce(() => {
 *       // Always uses the latest query value, even if this callback
 *       // was created when query was different
 *       const currentQuery = latestQuery.current;
 *       if (currentQuery) {
 *         onSearch(currentQuery);
 *       }
 *     }, 300),
 *     [onSearch] // Only depends on onSearch, not query
 *   );
 *
 *   useEffect(() => {
 *     debouncedSearch();
 *   }, [query, debouncedSearch]);
 *
 *   return (
 *     <input
 *       value={query}
 *       onChange={(e) => setQuery(e.target.value)}
 *     />
 *   );
 * };
 * ```
 */

export function useLatestRef<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}
